import type { Transaction, Budget, Account } from '@flows.cash/types'

/**
 * Calculate account balance from transactions with proper handling of transfer transactions
 * and credit accounts
 */
export function calculateBalance(transactions: Transaction[], accountType?: string): number {
  // Track processed transfer IDs to avoid double counting
  const processedTransferIds = new Set<string>();
  
  return transactions.reduce((total, transaction) => {
    // Ensure we're working with numbers and using absolute values
    const amount = Math.abs(Number(transaction.amount) || 0);
    const isCredit = accountType === 'credit';
    
    // For transfers, check if we've already processed this transfer pair
    if (transaction.metadata?.linkedTransactionId) {
      if (processedTransferIds.has(transaction.id) || 
          processedTransferIds.has(transaction.metadata.linkedTransactionId)) {
        // Skip if already processed
        return total;
      }
      
      // Mark this transfer as processed
      processedTransferIds.add(transaction.id);
      if (transaction.metadata.linkedTransactionId) {
        processedTransferIds.add(transaction.metadata.linkedTransactionId);
      }
    }
    
    // Check if it's an incoming transaction (positive amount or explicitly marked as incoming)
    if (transaction.type === 'income' || 
        (transaction.type === 'transfer' && transaction.amount > 0) || 
        transaction.metadata?.isIncoming || 
        transaction.metadata?.direction === 'in') {
      // Incoming money
      if (isCredit) {
        return total - amount; // Reduce debt for credit accounts
      } else {
        return total + amount; // Add to balance for regular accounts
      }
    } 
    // Check if it's an outgoing transaction (negative amount or explicitly marked as outgoing)
    else if (transaction.type === 'expense' || 
             (transaction.type === 'transfer' && transaction.amount < 0) || 
             transaction.metadata?.isOutgoing || 
             transaction.metadata?.direction === 'out') {
      // Outgoing money
      if (isCredit) {
        return total + amount; // Increase debt for credit accounts
      } else {
        return total - amount; // Reduce balance for regular accounts
      }
    }
    
    return total;
  }, 0)
}

export function calculateBudgetProgress(budget: Budget): number {
  const spent = budget.spent || 0;
  const amount = budget.amount || 0;
  
  if (amount === 0) return 0; // Avoid division by zero
  
  return (spent / amount) * 100;
}

export function calculateMonthlySpending(
  transactions: Transaction[],
  month: number,
  year: number
): number {
  return transactions
    .filter((transaction) => {
      const date = new Date(transaction.date)
      return (
        date.getMonth() === month &&
        date.getFullYear() === year &&
        transaction.type === 'expense'
      )
    })
    .reduce((total, transaction) => total + transaction.amount, 0)
}

export function calculateCategoryTotal(
  transactions: Transaction[],
  categoryId: string
): number {
  return transactions
    .filter((transaction) => transaction.category_id === categoryId)
    .reduce((total, transaction) => {
      switch (transaction.type) {
        case 'income':
          return total + transaction.amount
        case 'expense':
          return total - transaction.amount
        case 'transfer':
          // For transfers, use the amount sign to determine whether to add or subtract
          return transaction.amount >= 0 
            ? total + transaction.amount  // Incoming transfer
            : total - Math.abs(transaction.amount)  // Outgoing transfer
        default:
          return total
      }
    }, 0)
}

export function calculateSavingsRate(
  income: number,
  expenses: number
): number {
  if (income === 0) return 0
  return ((income - expenses) / income) * 100
}

export function calculateCompoundInterest(
  principal: number,
  rate: number,
  time: number,
  compoundingFrequency = 12
): number {
  const r = rate / 100
  return (
    principal *
    Math.pow(1 + r / compoundingFrequency, compoundingFrequency * time)
  )
}
